import {
	type FrontendPlacementMessage,
	type FrontendStage,
} from '@post-office/message-maker-browser/types';

import { useMessageInstanceEvent } from '@atlassian/message-instance-event-frontend-client/useMessageInstanceEvent';

import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';

import { noValidation } from '@post-office/message-maker-browser/message-helpers';

import type { CreatePlacementMessageConfig } from '@post-office/message-template-contracts/placement-v2';

import {
	type CustomPlacementProps,
	PlacementFrontend,
} from '@post-office/browser--confluence-whiteboards/ct-changeboarding-message';

import { useCallback } from 'react';

import { type JSX } from 'react/jsx-runtime';

export const ChangeboardingMessage = ({ children, dismiss }: CustomPlacementProps): JSX.Element => {
	const { messageInstanceClicked } = useMessageInstanceEvent();
	const { onCtaActioned, onClosed, onDeclined } = usePostOfficeUserInteractions();

	// Kept on the legacy messageInstanceClicked event: existing consumers of the first
	// render-prop argument haven't opted into the semantic callbacks below yet, and
	// switching this path would silently change their recorded interaction status.
	const handleDismiss = useCallback(() => {
		dismiss();

		void messageInstanceClicked({
			messageEventType: 'clicked',
			clickedElement: {
				clickIdentifier: 'dismiss',
				clickedElement: 'button',
				buttonType: 'primary',
			},
			action: {
				actionType: 'dismiss',
			},
			additionalMetadataFromFrontend: {},
		});
	}, [messageInstanceClicked, dismiss]);

	const handleCtaActioned = useCallback(() => {
		dismiss();
		void onCtaActioned({
			interactionIdentifier: 'dismiss',
			additionalMetadata: {},
		});
	}, [dismiss, onCtaActioned]);

	const handleClosed = useCallback(() => {
		dismiss();
		void onClosed({
			interactionIdentifier: 'dismiss',
			additionalMetadata: {},
		});
	}, [dismiss, onClosed]);

	const handleDeclined = useCallback(() => {
		dismiss();
		void onDeclined({
			interactionIdentifier: 'dismiss',
			additionalMetadata: {},
		});
	}, [dismiss, onDeclined]);

	return (
		<>
			{children(handleDismiss, {
				onCtaActioned: handleCtaActioned,
				onClosed: handleClosed,
				onDeclined: handleDeclined,
			})}
		</>
	);
};

export const MessageFrontend: (config: CreatePlacementMessageConfig) => FrontendPlacementMessage<{
	render: FrontendStage<CustomPlacementProps>;
}> = PlacementFrontend.createMessageFrontend({
	render: {
		validate: noValidation(),
		Component: ChangeboardingMessage,
	},
});
